// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  userPendingInvitations,
  userPendingLockRequests,
} from '../../../../store/user/userSelectors';
import { selectEquipmentGroups } from '../../../../store/equipment/equipmentSelectors';
import { acceptLockRequest, rejectLockRequest } from '../../../../store/equipment/equipmentActions';
import {
  acceptCompanyInvitation,
  declineCompanyInvitation,
} from '../../../../store/user/userActions';
import { acceptedCompanyInvitation } from '../../../../store/companies/companiesSlice';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';

import { CardLink } from '../../../UI/CardLink/CardLink';
import { PopOver } from '../../../UI/PopOver/PopOver';

import { Badge } from '../../../../../web-utils/src/UI/Component/React/V1/Badge/Badge';
import { Button } from '../../../../../web-utils/src/UI/Component/React/V1/Button/Button';

import styles from '../Navigation.module.css';
import button from '../../../UI/Button/Button.module.css';

export const UserNotifications = () => {
  const pendingInvitations = useSelector(userPendingInvitations, shallowEqual);
  const pendingLockRequests = useSelector(userPendingLockRequests, shallowEqual);
  const equipmentGroups = useSelector(selectEquipmentGroups);
  const groupName = useCallback(
    uuid => equipmentGroups?.find(eg => eg && eg.equip_group_uuid === uuid)?.name,
    [equipmentGroups]
  );

  const isMobile = useMediaQuery('(max-width: 768px)');

  const dispatch = useDispatch();

  const notificationsCount = pendingInvitations?.length + pendingLockRequests?.length;

  const handleInvitation = async (invitation, action, setIsOpen) => {
    const data = {
      company_uuid: invitation.company_uuid,
      member_email: invitation.member_email,
      token: invitation.token,
    };
    if (action === 'accept') {
      const response = await dispatch(acceptCompanyInvitation(data));
      if (acceptCompanyInvitation.fulfilled.match(response)) {
        dispatch(acceptedCompanyInvitation(response.payload));
      }
    } else if (action === 'decline') {
      await dispatch(declineCompanyInvitation(data));
    }
    setIsOpen(false);
  };

  const handleLockRequest = async (request, action, setIsOpen) => {
    const data = {
      lock_req_uuid: request.lock_req_uuid,
    };
    if (action === 'accept') {
      data.cascade = true;
      await dispatch(acceptLockRequest(data));
    } else if (action === 'decline') {
      await dispatch(rejectLockRequest(data));
    }
    setIsOpen(false);
  };

  const acceptDeclineButtons = (data, handler, setIsOpen) => (
    <>
      <Button
        onClick={() => handler(data, 'accept', setIsOpen)}
        iconType='isSuccess'
        color='success'
        stroke={`var(--color-success)`}
        round
      />
      <Button
        onClick={() => handler(data, 'decline', setIsOpen)}
        iconType='close'
        color='error'
        stroke={`var(--color-error)`}
        round
      />
    </>
  );

  return (
    <PopOver
      color=''
      iconType='notification'
      fill='var(--color-light)'
      stroke='none'
      hoverEffect={true}
      classname={button.btnNavigation}
      buttonText={
        <>
          {isMobile ? 'Notifications' : null}
          {!notificationsCount ? null : (
            <span className={styles.alert}>
              <Badge status={notificationsCount} variant='primary' round />
            </span>
          )}
        </>
      }
    >
      {({ setIsOpen }) => (
        <div className={styles.navItemContainer}>
          <div className={styles.padding}>
            {pendingInvitations?.length ? (
              <div>
                <h3 className={styles.borderBottom}>Invitations</h3>
                {pendingInvitations?.map((el, i) => (
                  <CardLink key={i} view={false} grid={false}>
                    <div className={styles.card}>
                      <p className={styles.text}>{el?.company?.company_name} invited you.</p>
                      {acceptDeclineButtons(el, handleInvitation, setIsOpen)}
                    </div>
                  </CardLink>
                ))}
              </div>
            ) : pendingLockRequests?.length ? (
              <div>
                <h3 className={styles.borderBottom}>Control Requests</h3>
                {pendingLockRequests?.map((el, i) => (
                  <CardLink key={i} view={false} grid={false}>
                    <div className={styles.card}>
                      <p className={styles.text}>
                        {el?.company_member?.user?.first_name} requested control of{' '}
                        {groupName(el?.equip_group_uuid)}.
                      </p>
                      {acceptDeclineButtons(el, handleLockRequest, setIsOpen)}
                    </div>
                  </CardLink>
                ))}
              </div>
            ) : (
              <div className={styles.item}>
                <p>No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </PopOver>
  );
};
